import * as React from 'react';

import { cn } from '../base/utils';

export type ToggleVariant = 'default' | 'outline';
export type ToggleSize = 'default' | 'sm' | 'lg';

export const toggleClassName = (input: {
  variant?: ToggleVariant;
  size?: ToggleSize;
  className?: string;
} = {}) => cn(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  input.variant === 'outline'
    ? 'border border-input bg-transparent shadow-sm hover:bg-accent hover:text-accent-foreground'
    : 'bg-transparent',
  input.size === 'sm' ? 'h-8 min-w-8 px-1.5' : input.size === 'lg' ? 'h-10 min-w-10 px-2.5' : 'h-9 min-w-9 px-2',
  input.className,
);

export const toggleVariants = toggleClassName;

export interface ToggleProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  pressed?: boolean;
  defaultPressed?: boolean;
  onPressedChange?: (pressed: boolean) => void;
  variant?: ToggleVariant;
  size?: ToggleSize;
}

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(({
  pressed,
  defaultPressed = false,
  onPressedChange,
  variant = 'default',
  size = 'default',
  className,
  disabled,
  onClick,
  type = 'button',
  ...props
}, ref) => {
  const [uncontrolledPressed, setUncontrolledPressed] = React.useState(defaultPressed);
  const resolvedPressed = pressed ?? uncontrolledPressed;
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={resolvedPressed}
      data-state={resolvedPressed ? 'on' : 'off'}
      disabled={disabled}
      className={toggleClassName({ variant, size, className })}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented || disabled) return;
        const next = !resolvedPressed;
        if (pressed === undefined) setUncontrolledPressed(next);
        onPressedChange?.(next);
      }}
      {...props}
    />
  );
});
Toggle.displayName = 'Toggle';
